"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { UiIconId } from "@/lib/game/uiIcons";
import { iconLabel } from "./GameIcon";
import { GameIcon, type GameTone } from "./GameIcon";

const TONE_CLASS: Record<GameTone, string> = {
  violet:
    "border-violet-900/40 bg-gradient-to-b from-violet-400 to-violet-700 text-white shadow-[0_5px_0_#4c1d95]",
  sky: "border-sky-900/40 bg-gradient-to-b from-sky-400 to-sky-600 text-white shadow-[0_5px_0_#0c4a6e]",
  amber:
    "border-amber-900/40 bg-gradient-to-b from-amber-300 to-amber-500 text-amber-950 shadow-[0_5px_0_#92400e]",
  rose: "border-rose-900/40 bg-gradient-to-b from-rose-400 to-rose-600 text-white shadow-[0_5px_0_#9f1239]",
  indigo:
    "border-indigo-900/40 bg-gradient-to-b from-indigo-400 to-indigo-700 text-white shadow-[0_5px_0_#312e81]",
  emerald:
    "border-emerald-900/40 bg-gradient-to-b from-emerald-400 to-emerald-600 text-white shadow-[0_5px_0_#065f46]",
  slate:
    "border-slate-900/50 bg-gradient-to-b from-slate-500 to-slate-700 text-white shadow-[0_5px_0_#1e293b]",
};

const SIZE_CLASS = {
  sm: "h-11 w-11 rounded-xl border-[3px]",
  md: "h-14 w-14 rounded-2xl border-[3px]",
  lg: "h-16 w-16 rounded-2xl border-4",
  xl: "h-[4.5rem] w-[4.5rem] rounded-3xl border-4",
} as const;

const ICON_SIZE = {
  sm: "sm" as const,
  md: "md" as const,
  lg: "lg" as const,
  xl: "xl" as const,
};

interface GameIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: UiIconId;
  tone?: GameTone;
  size?: keyof typeof SIZE_CLASS;
  label?: string;
  badge?: ReactNode;
  /** Toggle / tab selected state */
  selected?: boolean;
}

export function GameIconButton({
  icon,
  tone = "violet",
  size = "md",
  label,
  badge,
  selected,
  className = "",
  disabled,
  ...props
}: GameIconButtonProps) {
  const ariaLabel = label ?? iconLabel(icon);

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-pressed={props.role === "tab" ? undefined : selected}
      className={`game-icon-btn relative inline-flex shrink-0 items-center justify-center transition enabled:active:translate-y-[3px] enabled:active:shadow-[0_2px_0_rgba(0,0,0,0.35)] disabled:cursor-not-allowed disabled:opacity-45 ${SIZE_CLASS[size]} ${TONE_CLASS[tone]} ${selected ? "z-10 scale-105 ring-4 ring-white/75" : ""} ${className}`}
      {...props}
    >
      <GameIcon icon={icon} size={ICON_SIZE[size]} />
      {badge ? (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[10px] font-extrabold text-slate-800 shadow-md">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
